import { Timestamp } from 'firebase/firestore';
import moment from 'moment';

import HolidayRepository from '../repositories/HolidayRepository';

class HolidayService {
  constructor() {
    this.repository = new HolidayRepository();
  }

  /**
   * Validate holiday data
   * @param {Object} holidayData - Holiday data to validate
   * @returns {Object} Object with isValid (boolean) and errors (array)
   */
  static validateHoliday(holidayData) {
    const errors = [];

    if (!holidayData.holiday.name || holidayData.holiday.name.trim() === '') {
      errors.push('Holiday name is required');
    }

    if (!holidayData.holiday.date) {
      errors.push('Holiday date is required');
    }

    if (holidayData.holiday.name && holidayData.holiday.name.length > 100) {
      errors.push('Holiday name cannot exceed 100 characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  cleanHolidayData(holidayData) {
    // the date field if its a moment convert it to a date in the format YYYY-MM-DD
    const isMoment = moment.isMoment(holidayData.holiday.date);
    if (holidayData.holiday.date && isMoment) {
      holidayData.holiday.date = holidayData.holiday.date.toDate().toISOString().split('T')[0];
    }

    if (holidayData.holiday?.country) {
      holidayData.holiday.country = holidayData.holiday.country.toLowerCase();
    }

    return {
      ...holidayData,
      holiday: {
        ...holidayData.holiday,
      },
    };
  }

  /**
   * Create a new holiday
   * @param {Object} holidayData - Holiday data
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Created holiday
   */
  async createHoliday(holidayData, organizationId) {
    const validation = HolidayService.validateHoliday(holidayData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    holidayData = this.cleanHolidayData(holidayData);

    const holiday = {
      ...holidayData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    delete holiday.holiday.region;
    return this.repository.create(holiday, organizationId);
  }

  /**
   * Update an existing holiday
   * @param {string} holidayId - Holiday ID
   * @param {Object} holidayData - Updated holiday data
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Updated holiday
   */
  async updateHoliday(holidayId, holidayData, organizationId) {
    const validation = HolidayService.validateHoliday(holidayData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    holidayData = this.cleanHolidayData(holidayData);

    const holiday = {
      ...holidayData,
      updatedAt: Timestamp.now(),
    };

    return this.repository.update(holidayId, holiday, organizationId);
  }

  /**
   * Delete a holiday
   * @param {string} holidayId - Holiday ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<void>}
   */
  async deleteHoliday(holidayId, organizationId) {
    return this.repository.delete(holidayId, organizationId);
  }

  /**
   * Get a holiday by ID
   * @param {string} holidayId - Holiday ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Holiday data
   */
  async getHolidayById(holidayId, organizationId) {
    return this.repository.getHolidayById(holidayId, organizationId);
  }

  /**
   * Get all holidays for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} Array of holidays
   */
  async getAllHolidays(organizationId) {
    return this.repository.getAllHolidays(organizationId);
  }

  buildSyncableHoliday(holidayData) {
    return {
      holiday: {
        country: 'Chile',
        region: null,
        name: holidayData?.title,
        isOptional: false,
        date: holidayData?.date,
      },
      active: true,
    };
  }

  /**
   * Sync holidays from external API
   * @param {string} organizationId - Organization ID
   * @param {Array} externalHolidays - Array of holidays from external API
   * @returns {Promise<Object>} Sync result
   */
  async syncHolidays(organizationId) {
    try {
      const url = 'https://api.boostr.cl/holidays.json';
      const response = await fetch(url);
      const data = await response.json();

      const syncResults = {
        created: 0,
        updated: 0,
        errors: [],
      };

      if (data.data && Array.isArray(data.data)) {
        const existingHolidays = await this.getAllHolidays(organizationId);
        for (const externalHoliday of data.data) {
          const existingHoliday = existingHolidays.find(
            h => h.holiday.date === externalHoliday.date,
          );
          if (existingHoliday) continue;
          const holiday = this.buildSyncableHoliday(externalHoliday);
          if (holiday.holiday.region) delete holiday.holiday.region;

          await this.createHoliday(holiday, organizationId);
          syncResults.created++;
        }
      }

      return syncResults;
    } catch (error) {
      throw new Error(`Failed to sync holidays: ${error.message}`);
    }
  }
}

export default HolidayService;
